export const ADMIN_BANNER_NAVIGATION_ERROR_MESSAGE =
  'Banner links must use a valid internal Fork path beginning with /.';

export type AdminBannerNavigationValidation =
  | { valid: true; target: string | null }
  | { valid: false; target: null };

const APPROVED_STATIC_PATHS = new Set([
  '/',
  '/privacy',
  '/reset-password',
  '/hire/reset-password',
  '/seek/reset-password',
  '/contract-signing',
  '/hire/auth',
  '/hire/auth/callback',
  '/hire/complete-company-setup',
  '/hire/verify-email',
  '/dashboard',
  '/jobs',
  '/interviews',
  '/candidates',
  '/analytics',
  '/settings',
  '/settings/billing',
  '/settings/billing/credits',
  '/settings/billing/invoices',
  '/settings/billing/identity',
  '/organization',
  '/organization-setup',
  '/my-account',
  '/talent',
  '/talent/invitations',
  '/team',
  '/notifications',
  '/user-guide',
  '/change-password',
  '/force-reset-password',
  '/seek',
  '/seek/auth',
  '/seek/signin',
  '/seek/auth/callback',
  '/seek/signup',
  '/seek/verify-email',
  '/seek/onboarding',
  '/seek/dashboard',
  '/seek/profile',
  '/seek/cv',
  '/seek/jobs',
  '/seek/saved-jobs',
  '/seek/applications',
  '/seek/notifications',
  '/seek/settings',
  '/seek/settings/subscription',
  '/seek/settings/credits',
  '/seek/settings/invoices',
  '/seek/interview-practice',
  '/seek/account',
  '/admin/auth',
  '/super-admin',
  '/super-admin/services',
  '/super-admin/plans',
  '/super-admin/catalog',
  '/super-admin/plan-services',
  '/super-admin/plan-service-grid',
  '/super-admin/organizations',
  '/super-admin/ai-usage',
  '/super-admin/ai-prompts',
  '/super-admin/cv-design-library',
  '/super-admin/cv-design-library/editor',
  '/super-admin/email-templates',
  '/super-admin/consumption-overview',
  '/super-admin/identity-requests',
  '/super-admin/feature-flags',
  '/super-admin/action-log',
  '/super-admin/alerts',
  '/super-admin/add-ons',
  '/super-admin/credit-packs',
  '/super-admin/service-credit-pricing',
  '/super-admin/enterprise-deals',
  '/super-admin/credits',
  '/super-admin/subscriptions',
  '/super-admin/system-issues',
  '/super-admin/error-events',
  '/super-admin/test-issues',
  '/super-admin/shadow-access',
  '/super-admin/candidates',
  '/super-admin/ai-failures',
  '/super-admin/payment-simulator',
  '/super-admin/invoices',
  '/super-admin/finance-tools',
  '/super-admin/payment-recovery',
  '/super-admin/payment-reliability',
  '/super-admin/refunds',
  '/super-admin/stale-pending',
  '/super-admin/intro-pricing',
  '/super-admin/trials',
  '/super-admin/banners',
  '/super-admin/governance-ops',
  '/super-admin/governance-health',
  '/super-admin/governance-inspect',
  '/super-admin/deletion-reviews',
]);

const APPROVED_DYNAMIC_PATHS = [
  /^\/apply\/[^/]+$/u,
  /^\/interview\/[^/]+$/u,
  /^\/f2f\/[^/]+$/u,
  /^\/offer\/[^/]+$/u,
  /^\/invoices\/[^/]+$/u,
  /^\/jobs\/[^/]+$/u,
  /^\/interviews\/[^/]+$/u,
  /^\/candidates\/[^/]+$/u,
  /^\/checkout\/[^/]+$/u,
  /^\/seek\/applications\/[^/]+$/u,
  /^\/seek\/checkout\/[^/]+$/u,
  /^\/seek\/interview-practice\/[^/]+$/u,
  /^\/super-admin\/plan-services\/[^/]+$/u,
  /^\/super-admin\/organizations\/[^/]+$/u,
  /^\/super-admin\/candidates\/[^/]+$/u,
];

const ENCODED_SEPARATOR_OR_CONTROL = /%(?:0[0-9a-f]|1[0-9a-f]|2f|5c|7f)/i;
const VALID_PERCENT_ESCAPE = /%[0-9a-f]{2}/i;
const MALFORMED_PERCENT_ESCAPE = /%(?![0-9a-f]{2})/i;
const MAX_DECODE_LAYERS = 8;

function containsRawControlCharacter(value: string) {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f);
  });
}

function hasEncodedSeparatorOrControl(value: string) {
  let layer = value;

  for (let depth = 0; depth < MAX_DECODE_LAYERS; depth += 1) {
    if (ENCODED_SEPARATOR_OR_CONTROL.test(layer)) return true;
    if (!VALID_PERCENT_ESCAPE.test(layer)) return false;

    try {
      const decoded = decodeURIComponent(layer);
      if (decoded.includes('\\') || containsRawControlCharacter(decoded)) return true;
      if (decoded === layer) return false;
      layer = decoded;
    } catch {
      return true;
    }
  }

  return VALID_PERCENT_ESCAPE.test(layer);
}

function getRuntimeOrigin() {
  if (typeof window === 'undefined') return null;

  try {
    const origin = new URL(window.location.origin);
    if (origin.protocol !== 'http:' && origin.protocol !== 'https:') return null;
    return origin;
  } catch {
    return null;
  }
}

function isApprovedApplicationPath(pathname: string) {
  return APPROVED_STATIC_PATHS.has(pathname)
    || APPROVED_DYNAMIC_PATHS.some((pattern) => pattern.test(pathname));
}

/**
 * Accepts only exact, canonical routes served by Fork on the current origin.
 * The original target is returned byte-for-byte; unsafe input is never fixed up.
 */
export function validateAdminBannerNavigationTarget(value: unknown): AdminBannerNavigationValidation {
  if (value === null || value === undefined || value === '') return { valid: true, target: null };
  if (typeof value !== 'string') return { valid: false, target: null };
  if (!value.startsWith('/') || value.startsWith('//')) return { valid: false, target: null };
  if (value !== value.trim() || value.includes('\\') || containsRawControlCharacter(value)) {
    return { valid: false, target: null };
  }
  if (MALFORMED_PERCENT_ESCAPE.test(value) || hasEncodedSeparatorOrControl(value)) {
    return { valid: false, target: null };
  }

  const origin = getRuntimeOrigin();
  if (!origin) return { valid: false, target: null };

  try {
    const target = new URL(value, origin);
    const canonicalTarget = `${target.pathname}${target.search}${target.hash}`;

    if (target.origin !== origin.origin || target.username || target.password) {
      return { valid: false, target: null };
    }
    if (canonicalTarget !== value || target.pathname.includes('//')) {
      return { valid: false, target: null };
    }
    if (!isApprovedApplicationPath(target.pathname)) return { valid: false, target: null };

    return { valid: true, target: value };
  } catch {
    return { valid: false, target: null };
  }
}
